/**
 * SettingsAgentsPage — Landlord Portal
 *
 * Route: /settings/agents
 *
 * One card per playbook agent kind (Repair, Leasing): its default on/off + review switches, the
 * playbook chip and summary, and a link to the playbook page. The playbook summary loads once for
 * both cards; if it fails the switches still work and a retryable error block is shown.
 *
 * Then the Reference agent card: its on/off switch only (no review switch, no playbook — BR-28).
 * Start reference check on a lead's Verifications tab is unavailable while it is off.
 */

import { useCallback, useEffect, useState } from 'react';
import { getAgentPlaybooks } from '../api/agent-playbooks.api';
import { PageHeader } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { SettingsLayout } from '../components/settings/SettingsLayout';
import { AgentSettingsCard } from '../components/settings/AgentSettingsCard';
import { ReferenceAgentSettingsCard } from '../components/settings/ReferenceAgentSettingsCard';
import type { AgentPlaybookKindView, PlaybookAgentKind } from '@resida/api-types/agent';

const AGENT_KINDS: PlaybookAgentKind[] = ['repair', 'leasing'];

export function SettingsAgentsPage() {
  const [views, setViews] = useState<AgentPlaybookKindView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (isCancelled: () => boolean = () => false) => {
    setLoading(true);
    setFailed(false);
    try {
      const data = await getAgentPlaybooks();
      if (isCancelled()) return;
      setViews(data.playbooks);
    } catch {
      if (isCancelled()) return;
      setFailed(true);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  return (
    <SettingsLayout>
      <div className="px-[var(--page-padding-x)]" data-testid="settings-agents-page">
        <PageHeader
          title="AI agents"
          subtitle="Choose where each agent works by default and how closely you review it."
          subtitleWrap
        />

        {failed && (
          <div
            role="alert"
            data-testid="agent-playbooks-error"
            className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
          >
            <span>
              <span className="font-semibold text-destructive">We couldn't load your agents' playbooks.</span>{' '}
              Your agents keep working from the version you last saved.
            </span>
            <Button
              type="button"
              variant="outline"
              onClick={() => void load()}
              data-testid="agent-playbooks-retry"
            >
              Retry
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {AGENT_KINDS.map((kind) => (
            <AgentSettingsCard
              key={kind}
              agentKind={kind}
              view={views?.find((v) => v.agentKind === kind) ?? null}
              playbookLoading={loading}
            />
          ))}
          <ReferenceAgentSettingsCard />
        </div>
      </div>
    </SettingsLayout>
  );
}

export default SettingsAgentsPage;
